// Additional sales-order line items (NDB CPQ Additional Line Items workbook).
// On Save & Submit these ride along with the configured item as their own SO
// lines (qty 1 per configured unit -- per burner for an MB's burner parts, see
// buildAdditionalLines -- NetSuite's native price + tax, NOT priced like the Serial Tag).
// None of them are in the assembly BOM.
//
// Each is a lookup on config answers -> an item Name/Number (part number), which
// is then resolved to a NetSuite internal id (see resolveAdditionalLines):
//
//   Flange Gasket  — if Flange Gasket = Yes           -> by Flange OD
//                    (falls back to Firetube Size when Flange OD is blank). Its
//                    line description carries the flange bolt pattern the sheet is
//                    cut to (see fillGasketDescription).
//   Ext Nipple     — if Custom Nipple = Yes           -> by Main Assembly Size. Its
//                    line description gets "Flange to nozzle end = 31\"" added under the
//                    item's own text (see fillNippleDescription).
//   Main Burner    — Compound Injector / High Pressure SS: Type + Size + Mixer
//                    Orifice Material + Ferrofix flag. A Ferrofix nozzle -> the
//                    burner WITH the built-in Ferrofix nozzle (no separate nozzle
//                    line). Any other nozzle (or none) -> the "without Nozzle"
//                    burner PLUS a separate Main Nozzle line for the chosen nozzle.
//                    Mixer Only: Size + Material + Mixer Type (NS / HO / HO with
//                    Rod) + Body Height (HO with Rod only: <=16" vs >=18" housing
//                    height) -> the mixer, PLUS a Main Nozzle line for any chosen
//                    nozzle type (including Ferrofix).
//   Main Nozzle    — by Main Nozzle Type + Main Assembly Size (see above).
//   Pilot Assembly — by Pilot Type + Pilot Orifice Material, then the variant: Custom
//                    when the main burner has an extension nipple, else RHSB nozzle,
//                    capillary tube, or the standard pilot; Surefire by canister size
//                    and tube; #72, Steep and UV have their own items. See pilotLine.
//   Coupling       — the BLK Merchant coupling that joins the extension nipple to a
//                    Compound Injector, by Main Assembly Size.
//
// The lookup tables are transcribed from the workbook into additionalLineItems.json. The
// Pilot and Coupling rules were learned from the sales orders reps entered by hand and
// checked by a second, independent pass (scratch/predict/research/lines and verify-lines,
// 2026-10-17): legacy orders from 2025-07 on, pilot item right 92-95%; coupling 100%
// precision, 87-90% recall.

import type { AnswerState, Question } from "../types";
import { stripHtml } from "./text";
import { isFah } from "./fahSections";
// Formats a decimal back into the mixed-inch form dimensions are written in ("14-3/4").
import { normalizeMixedInches } from "./mappingDecoder";
import lookup from "../data/additionalLineItems.json";

export type AdditionalLineKind = "gasket" | "nipple" | "burner" | "nozzle" | "pilot" | "coupling";

// Per-config values that fill the labeled lines of a Main Burner / mixer item's
// description template (Orifice Size / Drill / PSI / BTU/HR / Internals). Computed
// from the config in buildAdditionalLines and applied to the item's fetched
// description in resolveAdditionalLines. Only the "burner" (mixer) kind carries it.
export type MixerDescFields = {
  orificeSize: string; // Small / Medium / Large, bucketed from the assembly size
  drill: string; // mixer orifice size, with an inch mark
  psi: string; // fuel pressure
  btu: string; // required heat duty (Btu/hr)
  internals: string; // mixer orifice material
};

// Per-config values that fill the labeled lines of a Flange Gasket item's
// description template. Every gasket SKU's stored description carries the sheet
// size followed by a BLANK labeled block — the sheet is cut to the flange's bolt
// pattern, so the shop needs that pattern on the line:
//
//   Flange Gasket - 1/16" x 28" x 28" - Sheet
//   Flange OD: 26
//   Flange ID: 20
//   Quantity: 16
//   Hole Size: 5/8
//   Bolt Hole Centers: 24
//
// Values render BARE (no inch mark), the way the pattern is written on the order.
// Only the "gasket" kind carries it; see fillGasketDescription for the two label
// spellings the SKUs use.
export type GasketDescFields = {
  flangeOd: string; // FLANGE_OD
  flangeId: string; // FLANGE_ID
  quantity: string; // BOLT_HOLE_QUANTITY
  holeSize: string; // BOLT_HOLE_SIZE, e.g. 5/8
  boltHoleCenters: string; // BOLT_HOLE_CENTERS (bolt circle)
};

export type AdditionalLineSpec = {
  kind: AdditionalLineKind;
  partNumber: string; // item Name/Number from the workbook (resolved to an id later)
  quantity: number;
  label: string; // human-readable, for logging only
  descFields?: MixerDescFields; // set on the "burner" kind, to fill its description
  gasketFields?: GasketDescFields; // set on the "gasket" kind, to fill its description
  flangeToNozzle?: string; // set on the "nipple" kind: the length its description is cut to, e.g. `31"`
  description?: string; // a whole line description, used as-is (the Custom pilot)
};
export type AdditionalLinePayload = {
  itemId: string;
  itemName: string;
  quantity: number;
  description?: string; // per-config line-description override (Main Burner / mixer / gasket / nipple / Custom pilot)
};

// A "Mixer Only" assembly is the mixer body, so its Bell/Rosebud/Sticktite/Ferrofix
// tip is a separate item -> add a nozzle line for whatever nozzle type is chosen.
// Flip to false if the shop says the nozzle is not sold separately for Mixer Only.
// (Compound Injector / High Pressure SS handle the nozzle differently: a Ferrofix
// nozzle is built into the burner; any other nozzle is a separate line — see below.)
export const MIXER_ONLY_ADDS_NOZZLE = true;

// One workbook row of the Flange Gasket sheet table: the gasket sheet that suits a
// given flange OD, with the firetube size that flange normally sits on (flange OD =
// firetube + 4) and the sheet's own size (flange OD + 2) kept alongside for lookup
// fallback and labelling.
type GasketRow = { firetube: number; flangeOd: number; sheet: number; itemId: string };

type LookupTables = {
  gasket: GasketRow[];
  nipple: Record<string, string>;
  burnerCiHp: Record<string, string>;
  burnerMixer: Record<string, string>;
  nozzle: Record<string, string>;
  pilot: Record<PilotVariant, { stainless: string; brass: string }>;
  coupling: Record<string, string>;
};
type PilotVariant =
  | "custom" | "rhsb" | "rhsbTube" | "tube" | "standard"
  | "orifice72" | "orifice72Tube" | "steep" | "uv"
  | "surefireHalf" | "surefireHalfTube" | "surefire2" | "surefire2Tube";
const PILOT_LABEL: Partial<Record<PilotVariant, string>> = {
  orifice72: "#72", orifice72Tube: "#72, capillary tube", steep: "Steep capillary tube", uv: "UV",
};
const L = lookup as LookupTables;

// Code of the currently-selected option of a radio question ("PILOT_STANDARD"), or "".
function selectedCode(answers: AnswerState, qCode: string): string {
  const prefix = `${qCode}/`;
  for (const [k, v] of Object.entries(answers)) if (v === true && k.startsWith(prefix)) return k.slice(prefix.length);
  return "";
}

// THE PILOT ASSEMBLY (2026-10-17). Reps add it by hand today; the rule is what they did
// (scratch/predict/research/lines/REPORT.md, checked independently in verify-lines/):
//
//   - Only when the pilot section was filled in. PILOT_TYPE defaults to Standard on startup,
//     so it says nothing alone; a pilot orifice MATERIAL is what shows the rep set the pilot
//     (Standard with no material -> no pilot on the order, 7 of 8). None / Fan Tail -> none.
//   - Stainless or brass from that material.
//   - Custom when the main burner has an extension nipple (CUSTOM_NIPPLE = Yes): 95% of
//     orders with a nipple get the Custom pilot since 2024-07, 8 of 709 without one. It wins
//     over RHSB -- the Custom item is nozzle-agnostic and the RHSB goes in its description,
//     which is what the shop reads (the wording reps use most).
//   - Else an RHSB nozzle, a #72 orifice, a Steep or STD capillary tube, or the standard pilot.
//   - Surefire: the canister size picks the family (No Canister sold as the 1/2" item, 2 of
//     2), a capillary tube the PLN-11 version.
//
// ONE PILOT PER HOUSING, an MB included: an MB item is a whole housing with 2 or 3 burners and
// one pilot inlet, and every legacy MB order with a pilot line had one per housing (15 of 15).
//
// #72, STEEP AND UV (Kevin, 2026-10-17: "add those pilots"; every past line of these items in
// scratch/predict/research/lines/60-held-pilots.cjs). They were left to the rep at first.
//   - #72 / #72 Long: the #72 items, named like the #70 ones (PLA-3-72 stainless, 41 quote/SO
//     lines; PLA-31-72 / PLA-21-72 with a capillary tube, 35). Both orifice answers sold the same
//     item. The brass item without a tube, PLA-2-72-S, exists but has never been sold.
//   - Steep capillary tube: PLA-21-70-SA / PLA-31-70-SA, the PLN-12 nozzle with the steep-angle
//     tube holder (brass 40 lines, stainless 1).
//   - UV Hookup + Sight Tube: the UV pilot assembly, 544622 brass / 547598 stainless (the pilot
//     plus the Y the scanner threads into). It wins over the Custom pilot: the one order with
//     both (Energy Transfer 202508-1208) sold the UV item and listed PLA-2-70-Custom in its
//     description, which is what uvCustomDescription writes. The "Box" UV items were never sold,
//     a box housing included (SLB BT-SB28/28-18).
//   - No line where no item exists: a #72 or RHSB pilot with a Steep tube, a #72 UV pilot, and
//     a Surefire with a Steep tube or UV.
const UV_PARTS = ['½" x Cl', '½" 45º', '1" Y-BMI', '1" x ½" Bushing', '1" x ½" Stainless Bell Reducer'];
function uvCustomDescription(metal: "stainless" | "brass", rhsb: boolean): string {
  const what = metal === "stainless" ? "Stainless" : "Brass";
  return [
    `UV - Pilot Assembly - ${what} - Standard`,
    "",
    "Consist of:",
    `${L.pilot.custom[metal]}${rhsb ? " W/ RHSB Nozzle" : ""}`,
    ...UV_PARTS,
    "",
    '1/2" Nipple to match main',
  ].join("\n");
}

function pilotLine(answers: AnswerState): { partNumber: string; label: string; description?: string } | null {
  const type = selectedCode(answers, "PILOT_TYPE");
  const material = selectedCode(answers, "PILOT_ORIFICE_MATERIAL");
  const metal = material === "STAINLESS_STEEL" ? "stainless" : material === "PILOT_ORIFICE_BRASS" ? "brass" : "";
  if (!metal) return null;
  const uv = selectedCode(answers, "UV_SETUP") === "FULL_UV_ASSEMBLY";
  const tube = selectedCode(answers, "CAPILLARY_TUBE_TYPE");
  const steep = tube === "CAPILLARY_TUBE_STEEP";
  const withTube = tube === "CAPILLARY_TUBE_STD";

  let variant: PilotVariant | null = null;
  if (type === "PILOT_SUREFIRE" || type === "PILOT_SUREFIRE_CANISTER_HALF" || type === "PILOT_SUREFIRE_CANISTER_2") {
    if (steep || uv) return null;
    const family = type === "PILOT_SUREFIRE_CANISTER_2" ? "surefire2" : "surefireHalf";
    variant = withTube ? (`${family}Tube` as PilotVariant) : (family as PilotVariant);
  } else if (type === "PILOT_STANDARD") {
    const rhsb = selectedCode(answers, "PILOT_NOZZLE_TYPE") === "PILOT_NOZZLE_RHSB";
    const orifice = selectedCode(answers, "PILOT_ORIFICE_SIZE");
    const is72 = orifice === "PILOT_ORIFICE_72" || orifice === "PILOT_ORIFICE_72_LONG";
    const custom = answers["CUSTOM_NIPPLE/CUSTOM_NIPPLE_YES"] === true;
    if (uv) {
      if (is72) return null;
      const label = `Pilot Assembly (UV, ${metal}${custom ? ", Custom" : ""})`;
      return custom
        ? { partNumber: L.pilot.uv[metal], label, description: uvCustomDescription(metal, rhsb) }
        : { partNumber: L.pilot.uv[metal], label };
    }
    if (custom) {
      const partNumber = L.pilot.custom[metal];
      const what = metal === "stainless" ? "Stainless" : "Brass";
      return {
        partNumber,
        label: `Pilot Assembly (Custom, ${what}${rhsb ? ", RHSB" : ""})`,
        description: `Pilot Assembly - ${what} - Custom ${rhsb ? 'W/ RHSB Nozzle & 1/2" Extension Nipple' : "With Extension Nipple"}`,
      };
    }
    if (steep && (rhsb || is72)) return null;
    if (rhsb) variant = withTube ? "rhsbTube" : "rhsb";
    else if (is72) variant = withTube ? "orifice72Tube" : "orifice72";
    else if (steep) variant = "steep";
    else variant = withTube ? "tube" : "standard";
  } else {
    return null;
  }
  return { partNumber: L.pilot[variant][metal], label: `Pilot Assembly (${PILOT_LABEL[variant] ?? variant}, ${metal})` };
}

// Label of the currently-selected option of a radio question (type 4), HTML
// stripped — e.g. MAIN_ASSEMBLY_TYPE -> "Compound Injector".
function selectedLabel(questions: Question[], answers: AnswerState, qCode: string): string {
  const q = questions.find((x) => x.custrecord_cpqc_question_code === qCode);
  if (!q) return "";
  for (const a of q._answers ?? []) {
    if (answers[`${qCode}/${a.custrecord_cpqc_answer_code}`] === true) {
      return (stripHtml(a.custrecord_cpqc_answer_label_html) || a.name || a.custrecord_cpqc_answer_code).trim();
    }
  }
  return "";
}

// Inch-dimension parser for the gasket lookup. FLANGE_OD / FIRETUBE_DIAMETER are
// free-text fields, so a size can arrive as a decimal ("21.375"), a mixed number
// ("21-3/8" — the form the mapping encoder normalises to), or a bare fraction,
// with or without an inch mark. Returns null for blank / unparseable / <= 0.
function parseInches(v: unknown): number | null {
  const s = String(v ?? "").replace(/["″\s,]/g, "");
  if (!s) return null;
  const mixed = s.match(/^(\d+)-(\d+)\/(\d+)$/);
  const frac = s.match(/^(\d+)\/(\d+)$/);
  const dec = s.match(/^\d+(?:\.\d+)?$/);
  let n: number;
  if (mixed) n = Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  else if (frac) n = Number(frac[1]) / Number(frac[2]);
  else if (dec) n = Number(s);
  else return null;
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Gasket rows by SHEET size ascending, so "the smallest sheet this flange can be cut
// from" is a first hit.
const GASKET_BY_SHEET = [...L.gasket].sort((a, b) => a.sheet - b.sheet);
const EPS = 1e-6; // float slack so an exact table size never steps up a size

/** How much bigger than the flange OD a sheet has to be before it can be cut from.
 *
 *  Kevin, 2026-09-03, on HCL-SB26/18-12-3: a 17-3/4" flange OD took the 18" sheet, which
 *  leaves 1/8" of material all round -- nothing you can cut a gasket out of. "Strictly
 *  larger" is the right SHAPE of rule (it is what stopped a 14-3/4" flange wasting the 18"
 *  sheet) but it has no idea how much room a cut needs, so an OD sitting just under a sheet
 *  size got a sheet it does not fit.
 *
 *  One inch satisfies both known rulings and changes NOTHING for the 23 listed sizes, since
 *  the table pairs every flange OD with a sheet 2" larger:
 *    14-3/4" -> 16" sheet (margin 1-1/4")   still the 16", as ruled on 2026-08-17
 *    17-3/4" -> 20" sheet (margin 2-1/4")   the 18" is now refused, margin 1/4"
 *  Raising this to 2 would make the rule "always OD + 2" and reverse the August ruling. */
const MIN_CUTTING_MARGIN = 1;

// The table's own relationship, from its header comment: a flange normally sits on a
// firetube 4" smaller. Used only when Flange OD is blank, so the firetube fallback goes
// through the SAME rule instead of a second, subtly different comparison.
const FLANGE_OD_OVER_FIRETUBE = 4;

/** Pick the gasket sheet for the current config, per the workbook's rules:
 *  keyed on Flange OD, since the flange (not the firetube) is what the gasket has
 *  to cover — a 16" firetube with a 22" flange takes the 24" sheet, not the 22".
 *  An off-table size rounds UP to the next sheet the workbook stocks (a 6.625"
 *  flange OD takes the 10" sheet). When Flange OD is blank we fall back to the
 *  workbook's Firetube Size column. A size past the largest sheet (50" flange OD /
 *  46" firetube) has no match — no gasket line, which the Additional Items audit
 *  view surfaces rather than guessing a sheet. */
function findGasketRow(answers: AnswerState): { row?: GasketRow; basis: string } {
  // The SAME flange OD the gasket's own description prints -- see gasketFlangeOd. With no
  // neck that is the bolt hole centers + 2, not the configured FLANGE_OD.
  //
  // Kevin, 2026-08-17: "if the neck height is none then the flange gasket we would show the
  // flange id as 10 and the flange od as 2 inches bigger than the bolt hole centers, or in
  // this case 14-3/4; the gasket would then be a 16x16."
  //
  // Deriving the SHEET from the same value as the DESCRIPTION is the point. They agreed by
  // luck on OT-SB24/12-10-2 (its stored FLANGE_OD is also 14-3/4), but nothing made them
  // agree: a configuration whose stored OD differs from bolt-hole-centers + 2 would have
  // printed one flange OD on the line and sized the sheet from another.
  const od = parseInches(gasketFlangeOd(answers));
  if (od != null) return { row: sheetFor(od), basis: `flange OD ${od}"` };
  const ft = parseInches(answers["FIRETUBE_DIAMETER/FIRETUBE_DIAMETER"]);
  if (ft != null) {
    const implied = ft + FLANGE_OD_OVER_FIRETUBE;
    return { row: sheetFor(implied), basis: `firetube ${ft}" (flange OD ${implied}")` };
  }
  return { basis: "no flange OD / firetube size" };
}

/** The smallest stocked sheet the flange can actually be cut from: at least
 *  MIN_CUTTING_MARGIN bigger than the flange OD, because a sheet the same size as the
 *  gasket -- or a hair over it -- leaves nothing to cut.
 *
 *  This was originally "round the flange OD up the table's flangeOd column", which
 *  reproduces the table for the 23 sizes the table lists but is wrong in between them: a
 *  14-3/4" flange OD stepped up to the 16" row and took an 18" sheet (544376) when a 16"
 *  sheet (544377) cuts it with room to spare. Kevin, 2026-08-17, on OT-SB24/12-10-2.
 *
 *  Then "strictly larger", which went too far the other way on an OD just under a sheet
 *  size -- see MIN_CUTTING_MARGIN. All three rules agree on all 23 table rows (asserted in
 *  test:additionallines), so none of this moves a flange whose OD is one of the listed
 *  sizes; they differ only in between.
 *
 *  Nothing above the largest sheet: a flange bigger than 51" has no stocked sheet that can
 *  be cut, and the Additional Items audit view surfaces the missing line rather than
 *  guessing. */
function sheetFor(flangeOd: number): GasketRow | undefined {
  return GASKET_BY_SHEET.find((r) => r.sheet - flangeOd >= MIN_CUTTING_MARGIN - EPS);
}

// Orifice Size bucket from the assembly size: 1 / 1-1/4 / 1-1/2 = Small,
// 2 / 2-1/2 = Medium, 3 / 4 / 5 = Large. The size label carries an inch mark
// (e.g. `1-1/2"`), so compare on the bare token; unknown sizes return "".
function orificeSizeBucket(sizeLabel: string): string {
  const s = String(sizeLabel).replace(/["″\s]/g, "");
  if (s === "1" || s === "1-1/4" || s === "1-1/2") return "Small";
  if (s === "2" || s === "2-1/2") return "Medium";
  if (s === "3" || s === "4" || s === "5") return "Large";
  return "";
}

// HO-with-Rod mixers come in two rod lengths keyed to the housing BODY height
// (Customer Requirements > Sizing Requirements > Housing Options > Height, i.e.
// the BURNER_TYPE "DIMENSIONS" cell): <=16" -> "16 and lower" (longer rod),
// >=18" -> "18 and higher" (shorter rod). NS/HO mixers are height-agnostic ("All").
// Housing heights are whole inches (DIMENSIONS_HEIGHT token map), so parsing the
// leading number is exact; an unknown/blank height returns "" (no HO-with-Rod match).
const DIMENSIONS_KEY_RE = /^BURNER_TYPE\/DIMENSIONS_\d+$/;
function housingBodyHeightBucket(answers: AnswerState): string {
  for (const k of Object.keys(answers)) {
    if (!DIMENSIONS_KEY_RE.test(k)) continue;
    const m = String(answers[k] ?? "").match(/[\d.]+/);
    if (!m) continue;
    const h = parseFloat(m[0]);
    if (Number.isFinite(h)) return h <= 16 ? "16 and lower" : "18 and higher";
  }
  return "";
}

// BTU/HR display: values under 1 million always show in full, comma-grouped
// (300,000 -> "300,000", 999,000 -> "999,000"). At 1 million and up, use the
// "N.NMM" abbreviation ONLY when it loses nothing — i.e. the value is an exact
// multiple of 100,000, so tenths-of-a-million is precise (2,000,000 -> "2.0MM",
// 2,500,000 -> "2.5MM"). Otherwise show the exact number, comma-grouped, so no
// digits are dropped (2,000,001 -> "2,000,001"). Blank / non-numeric input
// passes through untouched.
function formatBtuHr(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const n = Number(s.replace(/[,\s]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return s;
  if (n < 1_000_000) return n.toLocaleString("en-US");
  if (n % 100000 === 0) return (n / 1_000_000).toFixed(1) + "MM";
  return n.toLocaleString("en-US");
}

// Drill (mixer orifice size) display. An MTD drill designation — however the user
// typed it ("28 MTD", "MTD 28", "MTD Q") — renders as "MTD <size>" with no inch
// mark. A plain inch/fraction size ("7/16", "1/2") gets a trailing inch mark.
function formatDrill(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const mtd = s.match(/^MTD\s+(.+)$/i) || s.match(/^(.+?)\s+MTD$/i);
  if (mtd) return "MTD " + mtd[1].trim();
  return /["″]$/.test(s) ? s : s + '"';
}

// Strip a trailing inch mark so a bolt-pattern value reads as a bare dimension
// (FLANGE_ID's answer label is `20"`, BOLT_HOLE_SIZE's is `5/8"`), and read a
// "None" answer as no value at all — a blank leaves its template line untouched
// rather than writing "None" onto the gasket line.
function bareDimension(v: unknown): string {
  const s = String(v ?? "")
    .trim()
    .replace(/\s*["″]\s*$/, "")
    .trim();
  return /^none$/i.test(s) ? "" : s;
}

// Compute the per-config values that fill a Flange Gasket item's description
// template — the same flange bolt pattern the configured item's Sales Description
// renders as `14" Neck - 26" O.D.` / `16 (5/8) on a 24" B.C.`, from the same
// answers (FLANGE_OD / FLANGE_ID / BOLT_HOLE_QUANTITY / BOLT_HOLE_SIZE /
// BOLT_HOLE_CENTERS), so the two can't disagree.
/**
 * The Flange OD to print on the GASKET line, which is not always the configured
 * FLANGE_OD.
 *
 * With NO NECK (Kevin, 2026-08-12) the gasket is cut two inches larger than the bolt
 * hole centers, so that is what its description must say. Scoped deliberately to this
 * one description: the item's own FLANGE_OD field, the encoded mapping and the
 * configured line's Sales Description are all left exactly as they were, because the
 * instruction was that it "would only apply when filling the description on the sales
 * order".
 *
 * Falls back to the configured FLANGE_OD whenever the rule cannot be applied - a neck is
 * fitted, or the bolt hole centers are blank or non-numeric - so this can only ever
 * replace a value with a better one, never blank one out.
 */
function gasketFlangeOd(answers: AnswerState): string {
  const configured = bareDimension(answers["FLANGE_OD/FLANGE_OD"]);
  // "No neck" is the explicit NECK_NONE option, not merely an unanswered question:
  // NECK_HEIGHT carries no hide rule, so a real configuration always answers it, and
  // treating "not yet answered" as "no neck" would rewrite the OD mid-configuration.
  if (answers["NECK_HEIGHT/NECK_NONE"] !== true) return configured;
  // parseInches, NOT Number(). Bolt hole centers is a free-text dimension and is routinely
  // a mixed number -- "12-3/4" on OT-SB24/12-10-2 -- and Number("12-3/4") is NaN, so this
  // rule fell straight through to the configured OD on every fractional bolt circle. It has
  // therefore never fired since it was added on 2026-08-12. It LOOKED correct on that item
  // only because its stored FLANGE_OD is also 14-3/4, so the right number was printed for
  // the wrong reason.
  const bhc = parseInches(answers["BOLT_HOLE_CENTERS/BOLT_HOLE_CENTERS"]);
  if (bhc == null) return configured;
  // Printed as a mixed number ("14-3/4"), the way every other dimension on this line is
  // written -- a bare "14.75" would be correct and still look wrong on a shop document.
  return normalizeMixedInches(bhc + 2) ?? String(Number((bhc + 2).toFixed(4)));
}

function buildGasketDescFields(answers: AnswerState, questions: Question[]): GasketDescFields {
  return {
    flangeOd: gasketFlangeOd(answers),
    flangeId: bareDimension(selectedLabel(questions, answers, "FLANGE_ID")),
    quantity: bareDimension(selectedLabel(questions, answers, "BOLT_HOLE_QUANTITY")),
    holeSize: bareDimension(selectedLabel(questions, answers, "BOLT_HOLE_SIZE")),
    boltHoleCenters: bareDimension(answers["BOLT_HOLE_CENTERS/BOLT_HOLE_CENTERS"]),
  };
}

// Compute the per-config values that fill a mixer item's description template.
function buildMixerDescFields(answers: AnswerState, size: string, material: string): MixerDescFields {
  const mos = String(answers["MIXER_ORIFICE_SIZE/MIXER_ORIFICE_SIZE"] ?? "").trim();
  return {
    orificeSize: orificeSizeBucket(size),
    drill: formatDrill(mos),
    psi: String(answers["FUEL_PRESSURE/FUEL_PRESSURE"] ?? "").trim(),
    btu: formatBtuHr(answers["REQUIRED_HEAT_DUTY/REQUIRED_HEAT_DUTY"]),
    internals: material,
  };
}

/** Fill the labeled lines of an item's stored description template with per-config
 *  values, keeping every other line (header, "Includes:" list) exactly as stored.
 *  Only lines whose label matches a rule are rewritten — so a template that lacks
 *  a label passes through unchanged — and a blank computed value leaves the
 *  template line as-is (never blanks out a value the item already carries).
 *  The first matching rule wins, and CRLF is normalised to LF. */
function fillLabeledLines(description: string, rules: Array<{ re: RegExp; value: string }>): string {
  return String(description)
    .split(/\r?\n/)
    .map((line) => {
      for (const r of rules) {
        if (r.re.test(line)) return r.value ? line.replace(r.re, "$1 " + r.value) : line;
      }
      return line;
    })
    .join("\n");
}

/** Fill a mixer item's description template (Orifice Size / Drill / PSI / BTU/HR /
 *  Internals) from the config. A non-mixer description passes through unchanged. */
export function fillMixerDescription(description: string, f: MixerDescFields): string {
  return fillLabeledLines(description, [
    { re: /^(\s*Orifice Size:).*$/i, value: f.orificeSize },
    { re: /^(\s*Drill:).*$/i, value: f.drill },
    { re: /^(\s*PSI:).*$/i, value: f.psi },
    { re: /^(\s*BTU\/HR:).*$/i, value: f.btu },
    { re: /^(\s*Internals:).*$/i, value: f.internals },
  ]);
}

/** Fill a Flange Gasket item's description template (the flange bolt pattern) from
 *  the config. The 23 gasket SKUs are labeled two different ways — most read
 *  `Flange OD:` / `Flange ID:` / `Quantity:` / `Hole Size:` / `Bolt Hole Centers:`,
 *  while 544373 and 544377 read `Flange O.D:` / `Flange I.D:` / `Flange Pattern
 *  Quantity:` / `Flange Pattern Hole Size:` / `Flange Pattern Bolt Hole Centers:` —
 *  so each label is matched loosely enough to cover both (optional periods in
 *  O.D/I.D, optional `Flange Pattern` prefix). A SKU whose template has no such
 *  block keeps its description as stored. */
export function fillGasketDescription(description: string, f: GasketDescFields): string {
  return fillLabeledLines(description, [
    { re: /^(\s*Flange\s*O\.?\s*D\.?\s*:).*$/i, value: f.flangeOd },
    { re: /^(\s*Flange\s*I\.?\s*D\.?\s*:).*$/i, value: f.flangeId },
    { re: /^(\s*(?:Flange\s+Pattern\s+)?Quantity\s*:).*$/i, value: f.quantity },
    { re: /^(\s*(?:Flange\s+Pattern\s+)?Hole\s+Size\s*:).*$/i, value: f.holeSize },
    { re: /^(\s*(?:Flange\s+Pattern\s+)?Bolt\s+Hole\s+Centers\s*:).*$/i, value: f.boltHoleCenters },
  ]);
}

// The Flange to Nozzle End the extension nipple is cut to, written `31"` / `29-1/2"`, or "".
// The nine items built between 2026-09-01 and 2026-10-19 hold a fractional length in EXT_NIP
// rather than the list (see unlistedSizeTextFallback in products/ndb.ts), so that is the fallback.
function flangeToNozzleEnd(answers: AnswerState, questions: Question[]): string {
  const v =
    bareDimension(selectedLabel(questions, answers, "FLANGE_TO_NOZZLE_END")) ||
    bareDimension(answers["EXT_NIP/EXT_NIP"]);
  return v ? v + '"' : "";
}

/** Add the Flange to Nozzle End under an Ext Nipple item's stored description (Kevin,
 *  2026-10-22): `Nipple - BLK - 1" x Sch 40 x TBD`, a blank line, `Flange to nozzle end = 31"`.
 *  The item text is kept as stored; a "Flange to nozzle end" line already in it is replaced,
 *  never doubled. A blank length leaves the description as it was. */
export function fillNippleDescription(description: string, flangeToNozzle: string): string {
  if (!flangeToNozzle) return description;
  const base = String(description)
    .split(/\r?\n/)
    .filter((line) => !/^\s*Flange\s+to\s+nozzle\s+end\b/i.test(line))
    .join("\n")
    .trimEnd();
  const line = `Flange to nozzle end = ${flangeToNozzle}`;
  return base ? `${base}\n\n${line}` : line;
}

// The add-ons that come once per burner rather than once per housing (see buildAdditionalLines).
const PER_BURNER = new Set<AdditionalLineKind>(["burner", "nozzle", "nipple", "coupling"]);

/** Determine which additional line items this configuration brings into the SO. */
export function buildAdditionalLines(answers: AnswerState, questions: Question[]): AdditionalLineSpec[] {
  const lines: AdditionalLineSpec[] = [];
  const push = (
    kind: AdditionalLineKind,
    partNumber: string | undefined,
    label: string,
    // Per-config description fields for the kinds that have a template to fill
    // (burner -> descFields, gasket -> gasketFields), or a whole description (the Custom
    // pilot); omitted for the rest.
    extra?: { descFields?: MixerDescFields; gasketFields?: GasketDescFields; flangeToNozzle?: string; description?: string }
  ) => {
    if (partNumber) lines.push({ kind, partNumber, quantity: PER_BURNER.has(kind) ? burners : 1, label, ...extra });
  };

  // QUANTITY IS PER CONFIGURED UNIT (the order multiplies it by the configured line's quantity),
  // and an MB unit is a housing with 2 or 3 burners (Burner Qty "2 - MB" / "3 - MB"; "0 - FAH"
  // has none, "1 - SB" one). On legacy MB orders the burner parts come per burner -- mixer or
  // injector 19 of 21, Ext Nipple 14 of 14, Coupling 14 of 14, the nozzle with its burner --
  // while the Pilot (15 of 15), Flange Gasket (10 of 10) and Serial Tag come one per housing.
  // These went out at 1 per housing before 2026-10-17, half what an MB needs.
  const burnerQty = selectedCode(answers, "BURNER_QTY");
  const burners = burnerQty === "MAIN_BURNER_3" ? 3 : burnerQty === "MAIN_BURNER_2" ? 2 : 1;

  const size = selectedLabel(questions, answers, "BURNER_ASSEMBLY_SIZE");

  // 1. Flange Gasket — Flange Gasket = Yes, by Flange OD (see findGasketRow). Its
  // description template carries the flange bolt pattern the sheet is cut to.
  if (answers["FLANGE_GASKET/YES"] === true) {
    const { row, basis } = findGasketRow(answers);
    push("gasket", row?.itemId, `Flange Gasket ${row ? `${row.sheet}" sheet` : "(no sheet)"} — ${basis}`, {
      gasketFields: buildGasketDescFields(answers, questions),
    });
  }

  // 2. Ext Nipple — Custom Nipple = Yes, by Main Assembly Size.
  if (answers["CUSTOM_NIPPLE/CUSTOM_NIPPLE_YES"] === true) {
    push("nipple", L.nipple[size], `Ext Nipple (${size})`, { flangeToNozzle: flangeToNozzleEnd(answers, questions) });
  }

  // 3 & 4. Main Burner (+ Nozzle) by Main Assembly Type.
  // SUPPRESSED for an FAH config (BURNER_QTY = "0 - FAH"): an FAH has NO main
  // burner — everywhere else the Main Burner / Pilot / Ignitor sections hide and
  // their item fields transfer null (see isFah / fahSections.ts) — so no Main
  // Burner or Nozzle line may ride onto the order. Without this guard the stale,
  // now-hidden MAIN_ASSEMBLY_TYPE / MAIN_NOZZLE_TYPE answers left in state would
  // resurrect a burner line. (Flange Gasket / Ext Nipple above are housing-level,
  // independent of the burner, so they still apply on an FAH.)
  if (!isFah(answers)) {
    const type = selectedLabel(questions, answers, "MAIN_ASSEMBLY_TYPE");
    const material = selectedLabel(questions, answers, "MIXER_ORIFICE_MATERIAL");
    const nozzle = selectedLabel(questions, answers, "MAIN_NOZZLE_TYPE");

    // Values that fill the Main Burner / mixer item's description template (Orifice
    // Size / Drill / PSI / BTU/HR / Internals) — attached to the "burner" spec below.
    const mixerDescFields = buildMixerDescFields(answers, size, material);

    if (type === "Compound Injector" || type === "High Pressure SS") {
      // Ferrofix nozzle -> the burner with the built-in Ferrofix nozzle (ferrofix=1),
      // no separate nozzle line. Any other nozzle (or none) -> the "without Nozzle"
      // burner (ferrofix=0); and when a (non-Ferrofix) nozzle was actually chosen,
      // add it as its own Main Nozzle line by Nozzle Type + Main Assembly Size.
      const isFerrofix = nozzle === "Ferrofix";
      const ferrofix = isFerrofix ? "1" : "0";
      push(
        "burner",
        L.burnerCiHp[`${type}|${size}|${material}|${ferrofix}`],
        `${type} ${size} ${material}${isFerrofix ? " (Ferrofix)" : " (without Nozzle)"}`,
        { descFields: mixerDescFields }
      );
      if (!isFerrofix && nozzle) {
        push("nozzle", L.nozzle[`${nozzle}|${size}`], `${nozzle} nozzle (${size})`);
      }
      // 6. Coupling -- the BLK Merchant coupling that joins the extension nipple to the
      // injector barrel, so only a Compound Injector WITH the Ext Nipple above, sized like
      // both. On past orders: present 378 of 390 times with an injector and a nipple, sized
      // by the main assembly every time; a mixer threads straight onto the nipple (2 of 725)
      // and a High Pressure SS with a nipple never had one (0 of 3).
      if (type === "Compound Injector" && answers["CUSTOM_NIPPLE/CUSTOM_NIPPLE_YES"] === true) {
        push("coupling", L.coupling[size], `Coupling (${size})`);
      }
    } else if (type === "Mixer Only") {
      const mixerType = selectedLabel(questions, answers, "MIXER_TYPE"); // NS / HO / HO with Rod
      // Body Height only matters for HO with Rod (two rod lengths); NS/HO use "All".
      const bodyHeight = mixerType === "HO with Rod" ? housingBodyHeightBucket(answers) : "All";
      push(
        "burner",
        L.burnerMixer[`${size}|${material}|${mixerType}|${bodyHeight}`],
        `Mixer Only ${size} ${material} ${mixerType}${mixerType === "HO with Rod" ? ` (${bodyHeight || "?"})` : ""}`,
        { descFields: mixerDescFields }
      );
      // Mixer Only: the nozzle tip is a separate item -> add a nozzle line for any
      // chosen nozzle type (including Ferrofix).
      if (MIXER_ONLY_ADDS_NOZZLE && nozzle) {
        push("nozzle", L.nozzle[`${nozzle}|${size}`], `${nozzle} nozzle (${size})`);
      }
    }

    // 5. Pilot Assembly (see pilotLine). Inside the FAH guard like the burner: an FAH's pilot
    // section is hidden and keeps stale defaults. (OGI does buy a Surefire pilot with its FAH
    // housings, but the configuration holds no pilot answers to choose one from.)
    const pilot = pilotLine(answers);
    if (pilot) push("pilot", pilot.partNumber, pilot.label, pilot.description ? { description: pilot.description } : undefined);
  }

  // THE ORDER THESE LAND ON THE SALES ORDER, per Kevin's marked-up order (2026-08-17):
  //
  //     configured item · Serial Tag · Pilot Assembly · Mixer · Nozzle · Nipple · Coupling · Gasket
  //
  // (the coupling joins the nipple and the injector, and that is where reps put it).
  //
  // The blocks above are built in a different sequence (gasket first, because it is
  // housing-level and needs no burner values), which is why the mixer was landing near
  // the BOTTOM of the order instead of directly under the serial tag. Sorting the OUTPUT
  // rather than moving the blocks keeps each block next to the values it depends on --
  // `size`, `material` and the FAH guard are shared, and shuffling them to fix a display
  // order would be a real risk for a cosmetic gain.
  //
  // The configured item and the Serial Tag are not in this list; they are written ahead
  // of these lines by SaveSubmitButton, so they already lead.
  const KIND_ORDER: Record<string, number> = { pilot: 1, burner: 2, nozzle: 3, nipple: 4, coupling: 5, gasket: 6 };
  // A stable sort: two lines of the same kind keep the order the blocks produced them in
  // (the Compound Injector path can emit a burner and its nozzle together).
  return lines
    .map((line, i) => ({ line, i }))
    .sort((a, b) => (KIND_ORDER[a.line.kind] ?? 99) - (KIND_ORDER[b.line.kind] ?? 99) || a.i - b.i)
    .map((x) => x.line);
}

type ResolvedItems = Record<string, { id: string; description: string }>;

// PART NUMBER -> ITEM, kept for the session and warmed while the user configures (see
// SaveSubmitButton). It was ~1.1s at the front of an Existing Config save's wait, and the
// answer depends on the part numbers alone, which the configuration fixes long before submit.
//
// Only a SUCCESSFUL response is kept, the same rule as the price and the Serial Tag: a failed
// lookup is never reused, so a blip while configuring cannot cost the order its add-on lines.
// Expires so a session left open for hours re-reads the item records.
const ITEMS_TTL_MS = 10 * 60 * 1000;
const itemLookups = new Map<string, { at: number; p: Promise<ResolvedItems | null> }>();

function lookupItemsByName(names: string[]): Promise<ResolvedItems | null> {
  const key = [...names].sort().join(",");
  const hit = itemLookups.get(key);
  if (hit && Date.now() - hit.at < ITEMS_TTL_MS) return hit.p;
  const p = fetchItemsByName(names).then((r) => {
    if (!r) itemLookups.delete(key);
    return r;
  });
  itemLookups.set(key, { at: Date.now(), p });
  return p;
}

async function fetchItemsByName(names: string[]): Promise<ResolvedItems | null> {
  try {
    const res = await fetch(`/api/resolve-items?names=${encodeURIComponent(names.join(","))}`, {
      headers: { Accept: "application/json" },
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      items?: Array<{ id: string; itemid: string; description?: string }>;
    };
    if (res.ok && body.ok !== false && Array.isArray(body.items)) {
      const byName: ResolvedItems = {};
      for (const it of body.items) {
        byName[String(it.itemid)] = { id: String(it.id), description: String(it.description ?? "") };
      }
      return byName;
    }
  } catch {
    /* best effort — resolve failure just means no additional lines */
  }
  return null;
}

/** Warm the part-number lookup for this configuration's additional lines. Never throws. */
export function prefetchAdditionalLines(specs: AdditionalLineSpec[]): void {
  if (specs.length === 0) return;
  void lookupItemsByName([...new Set(specs.map((s) => s.partNumber))]);
}

/** Resolve the workbook part numbers to NetSuite internal ids via /api/resolve-items.
 *  Best-effort: unresolved part numbers are dropped (never blocks the submit). */
export async function resolveAdditionalLines(specs: AdditionalLineSpec[]): Promise<AdditionalLinePayload[]> {
  if (specs.length === 0) return [];
  const names = [...new Set(specs.map((s) => s.partNumber))];
  const byName: ResolvedItems = (await lookupItemsByName(names)) ?? {};
  const out: AdditionalLinePayload[] = [];
  for (const s of specs) {
    const hit = byName[s.partNumber];
    if (!hit || !hit.id) continue;
    const payload: AdditionalLinePayload = { itemId: hit.id, itemName: s.partNumber, quantity: s.quantity };
    // Fill the labeled lines of the item's stored description template from the
    // config: the Main Burner / mixer (Orifice Size / Drill / PSI / BTU/HR /
    // Internals) and the Flange Gasket (the flange bolt pattern the sheet is cut
    // to), and the Ext Nipple gets the Flange to Nozzle End under its own text. Other
    // kinds keep the item's native sourced description.
    if (s.description) payload.description = s.description;
    else if (hit.description) {
      if (s.descFields) payload.description = fillMixerDescription(hit.description, s.descFields);
      else if (s.gasketFields) payload.description = fillGasketDescription(hit.description, s.gasketFields);
      else if (s.flangeToNozzle) payload.description = fillNippleDescription(hit.description, s.flangeToNozzle);
    }
    out.push(payload);
  }
  return out;
}
